import type { PayableStage } from '../constants/stages.js';

/**
 * The guard rails on handing somebody a job — computed once, here, and never enforced.
 *
 * Both of these are ADVICE. An owner who wants to give a tenth job to the fastest karigar in the
 * shop, or ask the cutter to do a bit of finishing on a rush order, knows something the software
 * does not; a product that refuses them is a product they stop using by Thursday. So the
 * assignment always succeeds and the warning is shown next to it.
 *
 * It lives in shared code so the picker can grey-flag a name BEFORE the tap and the server can
 * return the same sentence AFTER it, without two different opinions about what "overloaded"
 * means.
 */

/** Live jobs (assigned or started, not yet finished) past which a person is called busy. */
export const ASSIGNMENT_OVERLOAD_THRESHOLD = 10;

export const ASSIGNMENT_WARNING_CODES = [
  'overloaded',
  'out_of_specialisation',
  'inactive',
] as const;
export type AssignmentWarningCode = (typeof ASSIGNMENT_WARNING_CODES)[number];

export interface AssignmentWarning {
  readonly code: AssignmentWarningCode;
  /** A sentence to show. The API sends one; the picker renders the same. */
  readonly message: string;
}

export interface AssignmentCandidate {
  readonly name: string;
  /** Jobs already open with this person. */
  readonly activeCount: number;
  /** The stages they normally handle. Empty means "no preference recorded", never "none". */
  readonly specialisations: readonly PayableStage[];
  readonly active?: boolean;
}

/**
 * What is worth saying about giving `candidate` a job at `stage`. Empty when nothing is.
 *
 * An empty `specialisations` list produces NO warning: a shop that has not filled the field in
 * has not told us anything, and warning on every assignment would train the owner to ignore the
 * warning that matters.
 */
export function assignmentWarnings(
  candidate: AssignmentCandidate,
  stage: PayableStage,
): AssignmentWarning[] {
  const warnings: AssignmentWarning[] = [];

  if (candidate.active === false) {
    warnings.push({
      code: 'inactive',
      message: `${candidate.name} no longer signs in to this shop`,
    });
  }

  if (candidate.activeCount >= ASSIGNMENT_OVERLOAD_THRESHOLD) {
    warnings.push({
      code: 'overloaded',
      message: `${candidate.name} already has ${String(candidate.activeCount)} jobs open`,
    });
  }

  if (candidate.specialisations.length > 0 && !candidate.specialisations.includes(stage)) {
    warnings.push({
      code: 'out_of_specialisation',
      message: `${candidate.name} does not usually do this stage`,
    });
  }

  return warnings;
}
